import { useEffect, useState } from "react";
import { AUTH_ERROR } from "@shared";
import { ApiFetchError, NETWORK_ERROR_CODE } from "../api/client";
import StatsApp from "../stats/StatsApp";
import * as api from "../stats/statsApi";

/**
 * Page /stat — le troisième rôle du projet, après le joueur et l'admin.
 *
 * Mot de passe DISTINCT de celui de l'administration. C'est tout l'intérêt : il
 * se partage avec qui doit lire les mesures, sans donner accès au contenu du
 * jeu, aux comptes, ni aux adresses courriel du concours.
 *
 * Aucun lien n'y mène depuis le jeu ni depuis l'admin : on y arrive en tapant
 * l'adresse, comme pour /admin.
 */
export default function StatsHome() {
  const [authentifie, setAuthentifie] = useState<boolean | undefined>(undefined);
  const [password, setPassword] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let annule = false;
    api
      .statsSession()
      .then((d) => {
        if (!annule) setAuthentifie(d.stats);
      })
      .catch(() => {
        if (!annule) setAuthentifie(false);
      });
    return () => {
      annule = true;
    };
  }, []);

  async function connexion(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (password === "" || busy) return;

    setBusy(true);
    setErreur(null);
    try {
      await api.statsLogin(password);
      setPassword("");
      setAuthentifie(true);
    } catch (raison) {
      const code = raison instanceof ApiFetchError ? raison.code : "";
      if (code === AUTH_ERROR.tooManyAttempts) {
        setErreur("Trop de tentatives. Réessaie dans quelques minutes.");
      } else if (code === NETWORK_ERROR_CODE) {
        setErreur("Serveur injoignable.");
      } else {
        setErreur("Mot de passe incorrect.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function deconnexion(): Promise<void> {
    await api.statsLogout().catch(() => undefined);
    setAuthentifie(false);
  }

  if (authentifie === undefined) {
    return (
      <main className="screen screen--admin screen--centered">
        <p className="card__text card__text--muted">Chargement…</p>
      </main>
    );
  }

  if (!authentifie) {
    return (
      <main className="screen screen--admin">
        <header className="screen__header">
          <h1 className="screen__title">Statistiques</h1>
        </header>

        <form className="card" onSubmit={(e) => void connexion(e)}>
          <label className="form__label" htmlFor="stats-password">
            Mot de passe des statistiques
          </label>
          <input
            id="stats-password"
            className="form__input"
            type="password"
            autoComplete="current-password"
            autoFocus
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setErreur(null);
            }}
          />

          {erreur !== null && <p className="form__error">{erreur}</p>}

          <button
            type="submit"
            className="button button--primary button--full"
            disabled={password === "" || busy}
          >
            {busy ? "Vérification…" : "Se connecter"}
          </button>

          <p className="card__text card__text--muted form__hint">
            Ce mot de passe n'est pas celui de l'administration. Il ouvre les mesures, et rien
            d'autre : aucun pseudo, aucune adresse courriel n'est accessible depuis cette page.
          </p>
        </form>
      </main>
    );
  }

  return (
    <main className="screen screen--admin screen--wide">
      <header className="screen__header">
        <h1 className="screen__title">Statistiques</h1>
      </header>
      <StatsApp onLogout={() => void deconnexion()} />
    </main>
  );
}
